/**
 * Controlled Input Component
 *
 * All-in-one component combining:
 * - React Hook Form Controller
 * - Ant Design Input
 * - FormFieldWrapper (label, required, error)
 *
 * Eliminates repetitive Controller + Input + label + error code.
 *
 * Usage:
 * <ControlledInput
 *   name="email"
 *   control={control}
 *   label="Email Address"
 *   required
 *   type="email"
 *   rules={{ required: 'Email is required', pattern: {...} }}
 * />
 */

import React from 'react';
import { Input } from 'antd';
import { Control, Controller, RegisterOptions, FieldValues, Path } from 'react-hook-form';
import { FormFieldWrapper } from './FormFieldWrapper';
import { useWizardStyles } from '@/styles/wizardStyles';

export interface ControlledInputProps<TFieldValues extends FieldValues = FieldValues> {
  /** Field name (must match form schema) */
  name: Path<TFieldValues>;

  /** React Hook Form control object */
  control: Control<TFieldValues>;

  /** Field label */
  label: string;

  /** Whether the field is required */
  required?: boolean;

  /** Validation rules */
  rules?: RegisterOptions<TFieldValues>;

  /** Input type (text, email, password, number, etc.) */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';

  /** Placeholder text */
  placeholder?: string;

  /** Help text to display below the input */
  help?: string;

  /** Whether the input is disabled */
  disabled?: boolean;

  /** Prefix icon or text */
  prefix?: React.ReactNode;

  /** Suffix icon or text */
  suffix?: React.ReactNode;

  /** Maximum length */
  maxLength?: number;

  /** Minimum value (for number type) */
  min?: number;

  /** Maximum value (for number type) */
  max?: number;

  /** Additional class name */
  className?: string;
}

/**
 * Controlled Input Component
 *
 * Provides a fully integrated form input with validation, labels, and error handling.
 */
export function ControlledInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  required = false,
  rules,
  type = 'text',
  placeholder,
  help,
  disabled = false,
  prefix,
  suffix,
  maxLength,
  min,
  max,
  className,
}: ControlledInputProps<TFieldValues>) {
  const styles = useWizardStyles();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <FormFieldWrapper
          label={label}
          required={required}
          error={error?.message}
          help={help}
          className={className}
        >
          <Input
            {...field}
            type={type}
            size="large"
            placeholder={placeholder}
            disabled={disabled}
            prefix={prefix}
            suffix={suffix}
            maxLength={maxLength}
            min={min}
            max={max}
            style={error ? styles.formInputError : styles.formInput}
          />
        </FormFieldWrapper>
      )}
    />
  );
}

export default ControlledInput;
